/**
 * Public "meet your lawyer" client (accountable-attorney-primitive §3.1).
 *
 * The pre-pay trust front door: an UNAUTHENTICATED, read-only, zero-data-capture
 * lookup of the named accountable attorney for a specific engagement, keyed by a
 * one-time magic token (mirrors the email-verification magic link — the server
 * looks the engagement up by `sha256(token) === meta.meetTokenHash`).
 *
 *   GET /api/meet/:token → { ok, meet: {…trust card…}, tenant?: {…branding…} }
 *
 * The card carries NOTHING the attorney can self-assert as "verified": the
 * bar-lookup link is server-composed to the STATE's own site, `verified` gates
 * only on `attorneys.barVerifiedAt`, and no photo/video/company badge appears.
 *
 * The endpoint is a sibling-repo build shipping in parallel; every caller here
 * degrades gracefully (loading / not-found / no-attorney-yet / error) so the
 * public route renders cleanly before the API lands.
 *
 * CONTRACT NOTE (house rule — no Swift built here): the public GET /meet/:token
 * response shape below and POST /engagements/:id/request-call must be registered
 * in avowal_api's src/openapi.ts and pass verify-clients.sh when the sibling API
 * ships them. This file only consumes the frozen shape.
 */
import type { TenantConfig } from './tenant';
import type { Deliverable, Engagement } from './engagements';

/** Server-composed state-bar lookup — the state's own site, the client's own click. */
export interface MeetVerify {
  stateName?: string | null;
  lookupUrl?: string | null;
  profileUrl?: string | null;
}

export interface MeetAttorney {
  displayName: string;
  barNumber?: string | null;
  barState?: string | null;
  verify?: MeetVerify | null;
  jurisdictions?: string[];
  firmName?: string | null;
  websiteUrl?: string | null;
  bio?: string | null;
  tagline?: string | null;
}

/** Dated e-signature warrant (reuses the engagement's attorneySignedAt, R4). */
export interface MeetESignature {
  willSignAs?: string | null;
  signedAt?: string | null;
}

export interface MeetFees {
  legalFeeCents?: number | null;
  platformFeeCents?: number | null;
  currency?: string;
  /** 'attorney_direct' under the locked custody model — never a platform trust. */
  custody?: string;
  note?: string;
}

export interface MeetScope {
  description?: string | null;
  deliverables?: Deliverable[];
}

/** The normalized trust-card shape rendered by <MeetYourLawyerCard>. */
export interface MeetCardData {
  attorney: MeetAttorney;
  /** true ONLY when the bar number is admin-verified (barVerifiedAt set). */
  verified?: boolean;
  eSignature?: MeetESignature | null;
  proofSample?: { url: string; caption?: string | null } | null;
  sla?: { hours?: number; promise?: string } | null;
  fees?: MeetFees | null;
  scope?: MeetScope | null;
  accountability?: string | null;
  call?: { available?: boolean } | null;
}

export type MeetResult =
  | { ok: true; meet: MeetCardData; tenant?: TenantConfig }
  | { ok: false; reason: 'not_found' | 'no_attorney' | 'error'; error?: string };

/** The default accountability line (CR6) when the API doesn't compose one. */
export function accountabilityLine(barState?: string | null): string {
  const where = barState ? `A licensed ${barState} attorney` : 'A licensed attorney';
  return `${where} — name and bar number, personally on the hook — reviews the law, decides, and signs. That attorney, not the software, is responsible to you.`;
}

/** Fetch the public meet-your-lawyer card by magic token. Never throws. */
export async function fetchMeet(token: string): Promise<MeetResult> {
  if (!token) return { ok: false, reason: 'not_found' };
  try {
    const res = await fetch(`/api/meet/${encodeURIComponent(token)}`, {
      headers: { Accept: 'application/json' },
    });
    if (res.status === 404 || res.status === 410) return { ok: false, reason: 'not_found' };
    const body = await res.json().catch(() => ({}));
    if (!res.ok || body.ok === false) {
      return { ok: false, reason: 'error', error: body.error ?? `HTTP ${res.status}` };
    }
    const meet = body.meet as MeetCardData | undefined;
    // Token resolved but no attorney is attached yet (Phase-1 supply inversion:
    // the engagement exists before the staff attorney-assign step binds one).
    if (!meet || !meet.attorney || !meet.attorney.displayName?.trim()) {
      return { ok: false, reason: 'no_attorney' };
    }
    return { ok: true, meet, tenant: body.tenant as TenantConfig | undefined };
  } catch (err) {
    return { ok: false, reason: 'error', error: (err as Error).message };
  }
}

/**
 * Build the same normalized trust card from an in-portal engagement (the
 * authenticated twin of /meet). The client Engagements surface renders this
 * above Sign/Pay so price + the named attorney are visible before commit.
 * Returns null when no attorney is attached (nothing to meet yet).
 */
export function engagementToMeetCard(e: Engagement): MeetCardData | null {
  const a = e.attorney;
  if (!a) return null;
  const legalFeeCents = e.feeAmountCents === null ? null : Number(e.feeAmountCents);
  return {
    attorney: {
      displayName: a.name,
      barNumber: a.barNumber ?? null,
      barState: a.barState ?? null,
      verify: a.verify ?? null,
      jurisdictions: a.jurisdictions ?? [],
      firmName: a.firmName ?? null,
      websiteUrl: a.websiteUrl ?? null,
      bio: a.bio ?? null,
      tagline: a.tagline ?? null,
    },
    verified: a.verified,
    eSignature: { willSignAs: a.name, signedAt: e.attorneySignedAt },
    proofSample: a.proofSampleUrl
      ? { url: a.proofSampleUrl, caption: a.proofSampleCaption ?? null }
      : null,
    sla: { hours: a.slaHours ?? 24 },
    fees: {
      legalFeeCents,
      platformFeeCents: e.meta?.platformFeeCents ?? null,
      currency: e.feeCurrency,
      custody: e.meta?.feeCustody ?? 'attorney_direct',
    },
    scope: { description: e.scopeDescription, deliverables: e.deliverables },
    accountability: accountabilityLine(a.barState),
    call: { available: true },
  };
}
